import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import CustomToast from '../../components/CustomToast';
import {
  convertAmountToWords,
  fetchPaymentGatewayConfig,
  fetchPaymentHistory,
  fetchPaymentStatus,
  formatInr,
  openHostedPayment,
  readPendingPayin,
  clearPendingPayin,
  startPayin,
  statusClass,
  statusLabel,
} from './paymentGatewayClient';
import './PaymentGateway.css';

const PaymentGatewayPayin = ({ user }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [config, setConfig] = useState(null);
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [returnTxn, setReturnTxn] = useState(null);
  const statusNotified = useRef('');

  const loadConfig = useCallback(async () => {
    try {
      const data = await fetchPaymentGatewayConfig();
      setConfig(data);
    } catch (error) {
      setConfig({
        enabled: false,
        payinEnabled: false,
        manualDepositEnabled: true,
      });
    }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const rows = await fetchPaymentHistory('payin', 8);
      setHistory(rows);
    } catch (error) {
      setHistory([]);
    }
  }, []);

  useEffect(() => {
    loadConfig();
    loadHistory();
  }, [loadConfig, loadHistory]);

  useEffect(() => {
    const fromReturn = readPendingPayin(searchParams);
    const fromHistory = (history.find((row) => row && row.status === 'pending' && row.merchantTxnId) || {}).merchantTxnId || '';
    const pgTxn = fromReturn || fromHistory;
    if (!pgTxn) return undefined;

    let cancelled = false;
    let tries = 0;

    const poll = async () => {
      try {
        const txn = await fetchPaymentStatus(pgTxn);
        if (!cancelled && txn) {
          setReturnTxn(txn);
          if (txn.status === 'completed' || txn.status === 'failed') {
            clearPendingPayin();
            if (statusNotified.current !== `${pgTxn}:${txn.status}`) {
              statusNotified.current = `${pgTxn}:${txn.status}`;
              if (txn.status === 'completed') {
                toast.success(
                  <CustomToast title="Deposit successful" description={`${formatInr(txn.amount)} has been credited.`} />
                );
              } else {
                toast.error(
                  <CustomToast title="Payment failed" description="The online payment did not complete." />
                );
              }
            }
            loadHistory();
            return;
          }
        }
      } catch (error) {
        // keep polling; webhook or fetch may still complete
      }

      tries += 1;
      if (!cancelled && tries < 36) {
        window.setTimeout(poll, 5000);
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [searchParams, loadHistory, history]);

  const clearReturn = () => {
    setReturnTxn(null);
    searchParams.delete('pgTxn');
    setSearchParams(searchParams, { replace: true });
  };

  if (!config || !config.payinEnabled) {
    return null;
  }

  const min = Number(config.minPayin || 100);
  const max = Number(config.maxPayin || 100000);
  const words = convertAmountToWords(amount);

  const handlePay = async (event) => {
    event.preventDefault();
    const value = parseInt(amount, 10);
    if (!Number.isInteger(value) || value < min || value > max) {
      toast.error(
        <CustomToast title="Invalid amount" description={`Enter a whole rupee amount between ${formatInr(min)} and ${formatInr(max)}.`} />
      );
      return;
    }

    setBusy(true);
    try {
      const txn = await startPayin({
        amount: value,
        redirectUrl: /localhost|127\.0\.0\.1/.test(window.location.hostname)
          ? undefined
          : window.location.origin,
      });
      const paymentUrl = txn && (txn.paymentUrl || txn.payment_url || txn.url);
      if (!paymentUrl) {
        toast.error(
          <CustomToast title="Payment not started" description="Try again or use bank transfer." />
        );
        setBusy(false);
        return;
      }
      openHostedPayment(paymentUrl);
    } catch (error) {
      toast.error(
        <CustomToast title="Payment not started" description={error.message || 'Try again or use bank transfer.'} />
      );
      setBusy(false);
    }
  };

  return (
    <div className="pg-card">
      <h3>Pay Online</h3>
      <p className="pg-sub">Instant UPI / card / net banking via payment gateway.</p>

      {returnTxn && (
        <div className={`pg-banner pg-banner--${returnTxn.status === 'completed' ? 'ok' : returnTxn.status === 'failed' ? 'fail' : 'pending'}`}>
          Payment {statusLabel(returnTxn.status).toLowerCase()} for {formatInr(returnTxn.amount)}.
          {returnTxn.status === 'pending' ? ' Waiting for confirmation from the gateway…' : ''}
          {' '}
          {returnTxn.status === 'pending' ? (
            <button
              type="button"
              onClick={async () => {
                try {
                  const txn = await fetchPaymentStatus(returnTxn.merchantTxnId);
                  if (txn) setReturnTxn(txn);
                } catch (error) {
                  toast.error(<CustomToast title="Status check failed" description={error.message || 'Try again in a few seconds.'} />);
                }
              }}
              style={{ border: 0, background: 'transparent', textDecoration: 'underline', cursor: 'pointer' }}
            >
              Check now
            </button>
          ) : null}
          {' '}
          <button type="button" onClick={clearReturn} style={{ border: 0, background: 'transparent', textDecoration: 'underline', cursor: 'pointer' }}>
            Dismiss
          </button>
        </div>
      )}

      <form onSubmit={handlePay}>
        <div className="pg-limits">
          <span>Min {formatInr(min)}</span>
          <span>Max {formatInr(max)}</span>
        </div>
        <div className="pg-field">
          <label>Amount (INR)</label>
          <input
            type="number"
            min={min}
            max={max}
            step="1"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
          />
          {words ? <div className="pg-words">{words}</div> : null}
        </div>
        <div className="pg-field">
          <label>Paying as</label>
          <input type="text" value={user?.clientName ? `${user.clientName} (${user.clientId || user.userId || ''})` : ''} readOnly disabled />
        </div>
        <button type="submit" className="pg-btn pg-btn--payin" disabled={busy}>
          {busy ? 'Opening payment…' : 'Pay now'}
        </button>
      </form>

      {history.length > 0 && (
        <div className="pg-history">
          <h4>Recent online deposits</h4>
          {history.map((row) => (
            <div className="pg-row" key={row.merchantTxnId}>
              <div>
                <div>{formatInr(row.amount)}</div>
                <div style={{ color: '#888', fontSize: '12px' }}>{row.merchantTxnId}</div>
              </div>
              <span className={`pg-status ${statusClass(row.status)}`}>{statusLabel(row.status)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PaymentGatewayPayin;
